import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useLanguage } from "../../contexts/LanguageContext";

// Fix for default marker icon in production builds
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface LocationPickerProps {
  value: { lat: string; lng: string; address?: string } | null;
  onChange: (location: { lat: string; lng: string; address?: string } | null) => void;
  error?: string;
  isOpen: boolean;
  onClose: () => void;
  governorate?: string; // Optional governorate to center the map
}

// Governorate coordinates for map centering
const GOVERNORATE_COORDS: Record<string, [number, number]> = {
  Baghdad: [33.3152, 44.3661],
  Basrah: [30.5085, 47.7835],
  Nineveh: [36.3350, 43.1189],
  Maysan: [31.8354, 47.1529],
  Diyala: [33.7497, 45.0000],
  "Al Anbar": [33.4256, 43.0003],
  Kirkuk: [35.4681, 44.3922],
  Saladin: [34.1766, 43.6739],
  Duhok: [36.8675, 42.9918],
  Erbil: [36.1911, 44.0094],
  Sulaymaniyah: [35.5608, 45.4333],
  Halabja: [35.1777, 45.9858],
  Najaf: [31.9996, 44.3314],
  Karbala: [32.6149, 44.0249],
  Babylon: [32.5425, 44.4200],
  Wasit: [32.4548, 45.8158],
  "Al Qadisiyah": [31.9970, 45.2700],
  "Al Muthanna": [29.9097, 45.3089],
  Dhiqar: [31.0586, 46.2600],
};

// Component to handle map clicks
const MapClickHandler: React.FC<{
  onClick: (lat: number, lng: number) => void;
}> = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to update map center when it changes
const MapCenterUpdater: React.FC<{
  center: [number, number];
}> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);

  return null;
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  error,
  isOpen,
  onClose,
  governorate,
}) => {
  const { t } = useLanguage();

  // Get center based on governorate or default to Baghdad
  const getDefaultCenter = (): [number, number] => {
    if (governorate && GOVERNORATE_COORDS[governorate]) {
      return GOVERNORATE_COORDS[governorate];
    }
    return [33.3152, 44.3661]; // Baghdad as fallback
  };

  const [mapCenter, setMapCenter] = useState<[number, number]>(getDefaultCenter());
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(
    value ? [parseFloat(value.lat), parseFloat(value.lng)] : null
  );
  const [tempMarkerPosition, setTempMarkerPosition] = useState<[number, number] | null>(markerPosition);
  const [tempAddress, setTempAddress] = useState<string | undefined>(value?.address);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  // Fetch address from coordinates using reverse geocoding
  const fetchAddress = async (lat: number, lng: number): Promise<string> => {
    try {
      setIsLoadingAddress(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${t("language_code") || "en"}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch address");
      }

      const data = await response.json();

      // Build a user-friendly address string
      const address = data.address;
      const parts = [];

      if (address.road) parts.push(address.road);
      if (address.neighbourhood) parts.push(address.neighbourhood);
      if (address.suburb) parts.push(address.suburb);
      if (address.city || address.town || address.village) {
        parts.push(address.city || address.town || address.village);
      }
      if (address.state) parts.push(address.state);
      if (address.country) parts.push(address.country);

      return parts.length > 0 ? parts.join(", ") : data.display_name;
    } catch (error) {
      console.error("Error fetching address:", error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Update marker when value prop changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (value && value.lat && value.lng) {
        const lat = parseFloat(value.lat);
        const lng = parseFloat(value.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          setTempMarkerPosition([lat, lng]);
          setTempAddress(value.address);
          setMapCenter([lat, lng]);
        }
      } else {
        setTempMarkerPosition(null);
        setTempAddress(undefined);
        setMapCenter(getDefaultCenter());
      }
    }
  }, [value, isOpen, governorate]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleMapClick = async (lat: number, lng: number) => {
    setTempMarkerPosition([lat, lng]);
    const address = await fetchAddress(lat, lng);
    setTempAddress(address);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert(t("geolocation_not_supported") || "Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setTempMarkerPosition([latitude, longitude]);
        setMapCenter([latitude, longitude]);
        setIsLoadingLocation(false);

        // Fetch address for current location
        const address = await fetchAddress(latitude, longitude);
        setTempAddress(address);
      },
      (error) => {
        console.error("Error getting location:", error);
        let errorMessage = t("location_error") || "Error getting your location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t("location_permission_denied") || "Location access was denied. Please enable location permissions.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t("location_unavailable") || "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = t("location_timeout") || "Location request timed out.";
            break;
        }

        alert(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleClearLocation = () => {
    setTempMarkerPosition(null);
    setTempAddress(undefined);
  };

  const handleConfirm = () => {
    if (tempMarkerPosition) {
      const location = {
        lat: tempMarkerPosition[0].toString(),
        lng: tempMarkerPosition[1].toString(),
        address: tempAddress,
      };
      setMarkerPosition(tempMarkerPosition);
      onChange(location);
    } else {
      setMarkerPosition(null);
      onChange(null);
    }
    onClose();
  };

  const handleCancel = () => {
    // Reset temp position to current saved value
    if (value && value.lat && value.lng) {
      const lat = parseFloat(value.lat);
      const lng = parseFloat(value.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        setTempMarkerPosition([lat, lng]);
        setTempAddress(value.address);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-semibold text-gray-800">
            {t("select_location") || "Select Location"}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleGetCurrentLocation}
              disabled={isLoadingLocation}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoadingLocation ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>{t("getting_location") || "Getting location..."}</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{t("use_current_location") || "Use Current Location"}</span>
                </>
              )}
            </button>
            {tempMarkerPosition && (
              <button
                type="button"
                onClick={handleClearLocation}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {t("clear_location") || "Clear"}
              </button>
            )}
          </div>

          {/* Address Display */}
          {tempMarkerPosition && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-sm text-blue-800 font-medium mb-2">
                {t("selected_location") || "Selected Location"}:
              </div>
              {isLoadingAddress ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                  <span>{t("fetching_address") || "Fetching address..."}</span>
                </div>
              ) : tempAddress ? (
                <div className="text-sm text-gray-800">
                  <p className="font-medium">{tempAddress}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {tempMarkerPosition[0].toFixed(6)}, {tempMarkerPosition[1].toFixed(6)}
                  </p>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <span className="font-mono">{tempMarkerPosition[0].toFixed(6)}, {tempMarkerPosition[1].toFixed(6)}</span>
                </div>
              )}
            </div>
          )}

          {/* Map */}
          <div className="rounded-lg overflow-hidden border-2 border-gray-300">
            <MapContainer
              center={mapCenter}
              zoom={13}
              style={{ height: "400px", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapCenterUpdater center={mapCenter} />
              <MapClickHandler onClick={handleMapClick} />
              {tempMarkerPosition && <Marker position={tempMarkerPosition} />}
            </MapContainer>
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <p className="text-sm text-gray-600 italic text-center">
              {t("location_picker_instruction") || "Click on the map to select a location, or use the GPS button to get your current location."}
            </p>
            {!tempMarkerPosition && (
              <p className="text-sm text-red-600 font-medium text-center">
                {t("location_required") || "Location selection is required to continue"}
              </p>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t("cancel") || "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!tempMarkerPosition}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
              tempMarkerPosition
                ? "bg-green-500 text-white hover:bg-green-600"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {t("confirm") || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default LocationPicker;
